/**
 * Starts a guest drag only once the pointer has actually travelled.
 *
 * Without a threshold, every *click* on a guest is also a zero-distance drag
 * that ends wherever the pointer already was — and releasing a seated guest
 * away from a chair means "unseat", so simply clicking someone in the roster
 * would silently remove them from their table. The same guard is what stops an
 * accidental click on a table from landing on the undo stack.
 *
 * Only the pointer that pressed counts, and not while two fingers are pinching:
 * the other finger of a pinch travels too, and must not lift a guest off a chair.
 */

import type { SeatRef } from '../store/planStore'
import { useViewStore } from '../store/viewStore'

const DRAG_THRESHOLD_PX = 4
/** A tap on a guest with a finger wanders further than a click does. */
const TOUCH_DRAG_THRESHOLD_PX = 10

export function beginGuestDrag(
  event: React.PointerEvent,
  guestId: string,
  from: SeatRef | null,
): void {
  if (event.button !== 0) return

  const pointerId = event.pointerId
  const threshold = event.pointerType === 'mouse' ? DRAG_THRESHOLD_PX : TOUCH_DRAG_THRESHOLD_PX
  const startX = event.clientX
  const startY = event.clientY
  let started = false

  const onMove = (move: PointerEvent) => {
    if (started || move.pointerId !== pointerId) return
    if (useViewStore.getState().pinching) {
      detach()
      return
    }
    if (Math.hypot(move.clientX - startX, move.clientY - startY) < threshold) return
    started = true
    detach()
    // GuestDragLayer owns the gesture from here.
    useViewStore.getState().startDrag({
      guestId,
      from,
      pointer: { x: move.clientX, y: move.clientY },
    })
  }

  const onUp = (up: PointerEvent) => {
    if (up.pointerId === pointerId) detach()
  }

  function detach() {
    window.removeEventListener('pointermove', onMove)
    window.removeEventListener('pointerup', onUp)
    window.removeEventListener('pointercancel', onUp)
  }

  window.addEventListener('pointermove', onMove)
  window.addEventListener('pointerup', onUp)
  window.addEventListener('pointercancel', onUp)
}
